/**
 * Git sync for a SCOPE's repository — "My boards & documents" or a team's
 * (git sync Stage 2, S2.3). The same steps as a workspace's
 * (workspaceGitSync.js) on the scope's own folder (scopeRepo.js): bring the
 * folder up to date, scan for secrets, commit with a readable message
 * ("2 documents, 1 task"), push; on divergence merge and push again; pull
 * merges the remote in.
 *
 * EVERY merge — a pull, a diverged push, a connect to a repository that
 * already holds work — hands what it changed to the contributors, who apply
 * it to the app (documents S2.4b, boards S2.5): otherwise the next refresh
 * would write the app's older state over it. A merge that meets conflicts is
 * resolved (keep mine / keep theirs per file) or cancelled — never left
 * half-done in the folder.
 *
 * `scope` = { key, name, git } — the scope key ("personal:<id>" / "team:<id>"),
 * its display name and its link (null until connected).
 */
import { invoke } from '@tauri-apps/api/core';
import { getProvider, providerOf, normalizeGitLink } from './providers';
import { DEFAULT_BRANCH, branchOfLink, requireToken, credArgs, authorFor, statusToken } from './gitAuth';
import { scanForSecrets } from './secretScan';
import { commitMessageFor } from './mirrorChanges';
import { listAllFiles, readTextSafe } from '../gitFileWriter';
import { isScopeDataPath } from './scopeRepoLayout';
import { scopeMirrorDir, refreshScopeMirror, saveScopeLink, applyScopeChanges, isCurrentScope } from './scopeRepo';
import { useTeamsStore } from '../../store/teamsStore';
import teamsService from '../teamsService';
import { adoptIfEmpty } from './scopeAdopt';
import { teamIdOf, isTeamAdminRole, targetsTeamRepo, sameRepo, teamRepoLabel, teamRepoFromLink } from './teamRepo';
import { APP_NAME } from '../../utils/appInfo';
import { reportScopeProgress, withScopeProgress, counter } from './scopeProgress';
import { pushMergingIfDiverged, sendAfterPull } from './scopePush';

/** The text files a commit of this scope would stage, scanned (file:line, never the value). */
export async function scanScopeForSecrets(dir, scopeKey) {
  const fs = await import('@tauri-apps/plugin-fs');
  const files = {};
  const rels = await listAllFiles(fs, dir, isScopeDataPath);
  const tick = counter(scopeKey, 'scanning', rels.length);
  for (const rel of rels) {
    const text = await readTextSafe(fs, `${dir}/${rel}`);
    if (text != null) files[rel] = text;
    tick();
  }
  return scanForSecrets(files);
}

const commitMessage = (dir, fallback) => commitMessageFor(dir, fallback, 'scope');

function requireCurrent(scope) {
  if (!isCurrentScope(scope?.key)) throw new Error('This link belongs to another account. Connect again while signed in as it.');
}

const teamRole = (teamId) => (useTeamsStore.getState().teams || []).find((t) => String(t.id) === teamId)?.role;

/** A team is connected, changed or disconnected by its owners and admins only (owner decision 02.10.2026). */
function requireTeamAdmin(scope) {
  const teamId = teamIdOf(scope?.key);
  if (!teamId) return;
  if (!isTeamAdminRole(teamRole(teamId))) throw new Error('Only the team owners and admins can connect it to a repository.');
}

/**
 * What a connect of this scope is (card 84628c27): 'personal'; for a team
 * 'join' (the repository stored on the team — any member), 'connect' (the
 * team has none yet — owners/admins) or 'move' (a different one than the
 * stored — owners/admins, and only with `move: true`, after the warning).
 * `scope.teamRepo` is the team's stored `{ url, provider }` (or null).
 */
function teamConnectKind(scope, opts) {
  const teamId = teamIdOf(scope?.key);
  if (!teamId) return 'personal';
  const role = teamRole(teamId);
  const stored = scope?.teamRepo?.url ? scope.teamRepo : null;
  if (stored && targetsTeamRepo(stored, opts)) {
    if (!role) throw new Error('You are not a member of this team.');
    return 'join';
  }
  if (!isTeamAdminRole(role)) {
    throw new Error(stored ? 'Only the team owners and admins can move the team to another repository.' : 'Only the team owners and admins can connect it to a repository.');
  }
  if (stored && !opts?.move) {
    const err = new Error(`This team already syncs with ${teamRepoLabel(stored)}. Join it, or use “Move the team to another repository”.`);
    err.code = 'TEAM_REPO_MOVE_NEEDS_CONFIRM';
    throw err;
  }
  return stored ? 'move' : 'connect';
}

/**
 * After an owner/admin connected (or moved) a team: the repository goes on the
 * team, so every member's device offers "Join". URL + provider only. A failure
 * leaves this device connected and is reported, never thrown.
 */
async function publishTeamRepo(scope, kind, git) {
  if (kind !== 'connect' && kind !== 'move') return {};
  try {
    if (kind === 'connect') {
      // This device did not know of one (offline, a stale list): never overwrite a repository a teammate stored meanwhile.
      const current = await teamsService.getTeamRepo(teamIdOf(scope.key)).catch(() => null);
      if (current?.url && sameRepo(current.url, git.url)) return { teamRepo: current };
      if (current?.url) {
        return {
          teamRepo: current,
          teamRepoError: `The team already syncs with ${teamRepoLabel(current)}; this device is now connected to ${teamRepoLabel(git)}. Use “Move the team to another repository” to change the team’s.`,
        };
      }
    }
    return { teamRepo: await teamsService.setTeamRepo(teamIdOf(scope.key), teamRepoFromLink(git)) };
  } catch (err) {
    return { teamRepoError: err?.message || String(err) };
  }
}

/*
 * One git operation per scope at a time: connect / sync / pull / resolve hold
 * the scope, and so does the background refresh (scopeGitAutoRefresh) — a
 * refresh in the middle of a pull would put the app's files between the
 * merge's reads.
 */
const busy = new Set();
export const isScopeBusy = (key) => busy.has(String(key));
export async function withScopeLock(key, work) {
  const k = String(key);
  while (busy.has(k)) await new Promise((r) => setTimeout(r, 50));
  busy.add(k);
  try {
    return await work();
  } finally {
    busy.delete(k);
  }
}

/** What goes in: documents (S2.4), boards (S2.5), projects (S2.6) — loaded on first use, they bring the document editor's converter. */
export async function ensureContributors() {
  (await import('./scopeDocuments')).registerDocumentsContributor();
  (await import('./scopeBoards')).registerBoardsContributor();
  (await import('./scopeProjects')).registerProjectsContributor();
}

/** The scope folder's data files (text) as they are now: path → text (also used by item history). */
export async function readDataFiles(dir) {
  const fs = await import('@tauri-apps/plugin-fs');
  const out = {};
  for (const rel of await listAllFiles(fs, dir, isScopeDataPath)) {
    const text = await readTextSafe(fs, `${dir}/${rel}`);
    if (text != null) out[rel] = text;
  }
  return out;
}

/** A merge that met conflicts, waiting for "keep mine / keep theirs": the files before it, to apply afterwards. */
const pendingMerges = new Map();
export const hasPendingMerge = (key) => pendingMerges.has(String(key));

export async function isScopeMerging(scopeKey) {
  const fs = await import('@tauri-apps/plugin-fs');
  return fs.exists(`${await scopeMirrorDir(scopeKey)}/.git/MERGE_HEAD`).catch(() => false);
}

/** Every problem the contributors reported for one apply. */
export const applyProblems = (applied) => Object.values(applied || {}).flatMap((r) => r?.problems || []);

/**
 * A merge whose changes could not ALL be applied to the app is undone (third
 * review): left merged, the next refresh would write the app's older state
 * over the merged files and the next sync would push that — a revert of the
 * teammate's work. Back at the commit before the merge, the remote stays
 * "behind" and the next pull merges and applies it again.
 */
async function undoMergeIfNotApplied(dir, headBefore, applied) {
  const problems = applyProblems(applied);
  if (!problems.length || !headBefore) return null;
  await invoke('git_reset_to', { repoPath: dir, oid: headBefore });
  return { status: 'not_applied', applied, problems };
}

/** Merge the remote in and apply what it changed; conflicts are kept for resolve / cancel. */
export async function mergeAndApply(scope, dir, args) {
  reportScopeProgress(scope.key, { step: 'merging' });
  const before = await readDataFiles(dir);
  const headBefore = (await invoke('git_status', { repoPath: dir, remote: 'origin', branch: args.branch }).catch(() => null))?.headOid || null; // GitStatus is camelCase on the wire
  // Without the commit to go back to, a merge that cannot be applied could not be undone: do not merge.
  if (!headBefore) throw new Error('The folder state could not be read — nothing was pulled. Try again.');
  const merge = await invoke('git_merge_remote', { repoPath: dir, ...args });
  if (merge?.status === 'conflicts') {
    pendingMerges.set(String(scope.key), { before, headBefore });
    return { status: 'conflicts', conflicts: merge.conflicts };
  }
  if (merge?.status !== 'fast_forward' && merge?.status !== 'merged') return { status: merge?.status || 'up_to_date' };
  const after = await readDataFiles(dir);
  const applied = await applyScopeChanges(scope.key, { before, after, dir });
  return (await undoMergeIfNotApplied(dir, headBefore, applied)) || { status: merge.status, applied };
}

/**
 * Connect a scope to a repository (created when missing and the provider
 * can) and push what it holds.
 * @returns {Promise<{ status: 'ok'|'diverged'|'secrets'|'conflicts', git: object, findings?: Array, applied? }>}
 */
async function connectScopeNow(
  scope,
  { owner, repo, createIfMissing = true, isPrivate = true, allowSecrets = false, provider: providerId, accountId, url: remoteUrl, vaultItemId, vaultItemName, move = false } = {}
) {
  requireCurrent(scope);
  const kind = teamConnectKind(scope, { owner, repo, url: remoteUrl, move });
  // Joining never creates a repository: it is the team's, and it exists.
  const create = createIfMissing && kind !== 'join';
  const provider = getProvider(providerId);
  const ctx = { provider: provider.id, accountId, url: remoteUrl, vaultItemId, vaultItemName };
  const token = await requireToken(provider, ctx);
  if (!owner || !repo) throw new Error('Repository owner and name are required');

  const dir = await scopeMirrorDir(scope.key);
  await ensureContributors();
  await refreshScopeMirror(scope.key, scope.name);
  if (create && provider.abilities.createRepo) {
    await provider.ensureRepoExists(token, owner, repo, isPrivate, ctx);
  }

  const author = await authorFor(provider, ctx);
  const url = await provider.repoUrl(owner, repo, ctx);
  // The account's server decides the URL: a GitLab account on another server is not the team's repository.
  if (kind === 'join' && !sameRepo(url, scope.teamRepo.url)) {
    throw new Error(`That account reaches ${teamRepoLabel({ url })}, not the team’s repository ${teamRepoLabel(scope.teamRepo)}. Choose an account on that server, or a Vault item.`);
  }
  const git = normalizeGitLink(
    { owner, repo, branch: DEFAULT_BRANCH, url, provider: provider.id, ...(vaultItemId ? { vaultItemId, vaultItemName: vaultItemName || '' } : {}) },
    { accountId: accountId || author.authorName || undefined }
  );

  if (!allowSecrets) {
    const findings = await scanScopeForSecrets(dir, scope.key);
    if (findings.length > 0) return { status: 'secrets', findings, git };
  }

  await invoke('git_init_or_open', { repoPath: dir, branch: DEFAULT_BRANCH });
  await invoke('git_set_remote', { repoPath: dir, name: 'origin', url });
  const cred = credArgs(provider, token, git);
  // An empty scope joining a filled repository takes it as it is: no starting commit, no merge (card 01fee74a).
  const adopted = await adoptIfEmpty(scope, dir, { cred, branch: DEFAULT_BRANCH, readDataFiles });
  if (adopted) {
    await saveScopeLink(scope.key, git);
    if (adopted.status === 'not_applied') return { ...adopted, git };
    return { status: 'ok', git, applied: adopted.applied, adopted: true, kind, ...(await publishTeamRepo(scope, kind, git)) };
  }
  reportScopeProgress(scope.key, { step: 'committing' });
  await invoke('git_commit_all', { repoPath: dir, message: await commitMessage(dir, `${scope.name} from ${APP_NAME}`), ...author });
  // The repository may already hold work (a teammate connected first): merge it in — and apply it — before pushing.
  reportScopeProgress(scope.key, { step: 'pushing' });
  let push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch: DEFAULT_BRANCH });
  let applied;
  if (push.status === 'diverged') {
    await saveScopeLink(scope.key, git);
    const merged = await mergeAndApply(scope, dir, { ...cred, remote: 'origin', branch: DEFAULT_BRANCH, ...author });
    if (merged.status === 'conflicts') return { ...merged, git, ...(await publishTeamRepo(scope, kind, git)) };
    if (merged.status === 'not_applied') return { ...merged, git };
    applied = merged.applied;
    push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch: DEFAULT_BRANCH });
  }
  await saveScopeLink(scope.key, git);
  return { status: push.status === 'diverged' ? 'diverged' : 'ok', git, applied, kind, ...(await publishTeamRepo(scope, kind, git)) };
}

/**
 * Commit + push what the scope holds now; on divergence, merge (and apply), then push.
 * @returns {Promise<{ status: 'ok'|'conflicts'|'diverged'|'secrets', conflicts?: string[], findings?: Array, applied? }>}
 */
async function pushScopeNow(scope, { allowSecrets = false } = {}) {
  if (!scope?.git) throw new Error('NOT_CONNECTED');
  requireCurrent(scope);
  const provider = providerOf({ git: scope.git });
  const token = await requireToken(provider, scope.git);
  const cred = credArgs(provider, token, scope.git);
  const branch = branchOfLink(scope.git);
  const dir = await scopeMirrorDir(scope.key);
  await ensureContributors();
  await refreshScopeMirror(scope.key, scope.name);

  if (!allowSecrets) {
    const findings = await scanScopeForSecrets(dir, scope.key);
    if (findings.length > 0) return { status: 'secrets', findings };
  }
  const author = await authorFor(provider, scope.git);
  reportScopeProgress(scope.key, { step: 'committing' });
  await invoke('git_commit_all', { repoPath: dir, message: await commitMessage(dir, `Update ${scope.name} from ${APP_NAME}`), ...author });

  // Nothing new to commit (git_commit_all → null, no empty commit): a version already waiting is still sent.
  return pushMergingIfDiverged(scope, dir, { cred, branch, author, merge: mergeAndApply });
}

/**
 * Pull: what the app holds now is committed first (secret scan as on push —
 * nothing is committed past it), then the remote is merged in, what the
 * merge changed is applied to the app by each contributor, and what is then
 * ahead of the remote is pushed (scopePush.js).
 * @returns {Promise<{ status: 'up_to_date'|'fast_forward'|'merged'|'conflicts'|'not_applied'|'secrets', conflicts?, findings?, applied?, sent?, sendError? }>}
 */
async function pullScopeNow(scope, { allowSecrets = false } = {}) {
  if (!scope?.git) throw new Error('NOT_CONNECTED');
  requireCurrent(scope);
  const provider = providerOf({ git: scope.git });
  const token = await requireToken(provider, scope.git);
  const cred = credArgs(provider, token, scope.git);
  const branch = branchOfLink(scope.git);
  const dir = await scopeMirrorDir(scope.key);
  const author = await authorFor(provider, scope.git);
  await ensureContributors();
  await refreshScopeMirror(scope.key, scope.name);

  if (!allowSecrets) {
    const findings = await scanScopeForSecrets(dir, scope.key);
    if (findings.length > 0) return { status: 'secrets', findings };
  }
  reportScopeProgress(scope.key, { step: 'committing' });
  await invoke('git_commit_all', { repoPath: dir, message: await commitMessage(dir, `Update ${scope.name} from ${APP_NAME}`), ...author });
  const pulled = await mergeAndApply(scope, dir, { ...cred, remote: 'origin', branch, ...author });
  // The commit above (and a merge of both sides) is sent too — a pull never leaves a version behind (07.10.2026).
  return sendAfterPull(scope, dir, pulled, { cred, branch, author, merge: mergeAndApply });
}

/**
 * Finish a merge that met conflicts: each file kept from this side ("local")
 * or the repository ("remote"), committed with both parents, applied to the
 * app, pushed.
 * @param {{path: string, side: 'local'|'remote'}[]} resolutions
 */
async function resolveScopeConflictsNow(scope, resolutions) {
  requireCurrent(scope);
  const provider = providerOf({ git: scope.git });
  const token = await requireToken(provider, scope.git);
  const branch = branchOfLink(scope.git);
  const dir = await scopeMirrorDir(scope.key);
  const author = await authorFor(provider, scope.git);
  const pending = pendingMerges.get(String(scope.key));
  await invoke('git_resolve', { repoPath: dir, resolutions, remote: 'origin', branch, ...author });
  pendingMerges.delete(String(scope.key));
  const applied = pending ? await applyScopeChanges(scope.key, { before: pending.before, after: await readDataFiles(dir), dir }) : undefined;
  const undone = await undoMergeIfNotApplied(dir, pending?.headBefore, applied);
  if (undone) return undone;
  const push = await invoke('git_push', { repoPath: dir, ...credArgs(provider, token, scope.git), remote: 'origin', branch });
  return { status: push.status === 'ok' ? 'ok' : 'diverged', applied };
}

/** Cancel a merge that met conflicts: the folder goes back to the app's own last commit. */
/** The same, for a caller that already holds the scope (the background refresh). */
export async function abortScopeMergeNow(scopeKey) {
  pendingMerges.delete(String(scopeKey));
  await invoke('git_merge_abort', { repoPath: await scopeMirrorDir(scopeKey) });
}

/*
 * A personal scope tracked on this device only (T10, 04.10.2026) goes to
 * scopeLocalGitSync.js: "This device only" starts its history, Sync now /
 * Save version only commits, Pull has nothing to get. Loaded on use (it
 * imports this module).
 */
const isLocal = (git) => git?.provider === 'local';
const local = () => import('./scopeLocalGitSync');

// Each user-started operation shows its steps (scopeProgress.js) until it ends.
const shown = (scope, work) => withScopeProgress(scope?.key, work);
export const connectScope = (scope, opts) =>
  shown(scope, () =>
    opts?.provider === 'local' ? local().then((m) => m.trackScopeLocally(scope, opts)) : withScopeLock(scope?.key, () => connectScopeNow(scope, opts))
  );
export const pushScope = (scope, opts) =>
  shown(scope, () => (isLocal(scope?.git) ? local().then((m) => m.saveScopeVersion(scope, opts)) : withScopeLock(scope?.key, () => pushScopeNow(scope, opts))));
export const pullScope = (scope, opts) =>
  isLocal(scope?.git) ? Promise.resolve({ status: 'up_to_date' }) : shown(scope, () => withScopeLock(scope?.key, () => pullScopeNow(scope, opts)));
export const resolveScopeConflicts = (scope, resolutions) => withScopeLock(scope?.key, () => resolveScopeConflictsNow(scope, resolutions));
export const abortScopeMerge = (scopeKey) => withScopeLock(scopeKey, () => abortScopeMergeNow(scopeKey));

/** Ahead / behind / local changes of the scope's folder (fetches first when it can; never for a local history). */
export async function scopeStatus(scope, { fetch = true } = {}) {
  if (!scope?.git) return null;
  const provider = providerOf({ git: scope.git });
  const dir = await scopeMirrorDir(scope.key);
  const branch = branchOfLink(scope.git);
  // A local history has no remote to ask — status only, nothing on the network.
  const remote = fetch && !provider.localOnly && !isLocal(scope.git);
  // A token behind a locked Vault: no fetch, no prompt — "Unlock the Vault to sync".
  const { token, vaultLocked } = !remote || provider.usesVault ? { token: '', vaultLocked: false } : await statusToken(provider, scope.git);
  if (remote && (token || scope.git.vaultItemId)) {
    try {
      await invoke('git_fetch', { repoPath: dir, ...credArgs(provider, token, scope.git), remote: 'origin', branch });
    } catch {
      /* offline: the last known state */
    }
  }
  const status = await invoke('git_status', { repoPath: dir, remote: 'origin', branch });
  return vaultLocked && status ? { ...status, vaultLocked: true } : status;
}

/**
 * Disconnect: forget the link ON THIS DEVICE (the folder and the remote
 * repository stay as they are). The repository stored on the team stays too —
 * only removeTeamRepo clears that.
 */
export async function disconnectScope(scope) {
  requireTeamAdmin(scope);
  await saveScopeLink(scope.key, null);
}

/** "Remove the team's repository": owners/admins; every device's own link stays as it is. */
export async function removeTeamRepo(scope) {
  const teamId = teamIdOf(scope?.key);
  if (!teamId) throw new Error('Only a team has a shared repository.');
  requireTeamAdmin(scope);
  await teamsService.clearTeamRepo(teamId);
}
